import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

declare global {
  interface Window {
    __LAST_ROUTE__?: string;
    __ENABLED_FEATURES__?: Array<string> | Record<string, boolean>;
    __CURRENT_USER__?: {
      id?: string | number;
      email?: string;
      name?: string;
      role?: string;
      tenant?: string;
      state?: string;
    };
  }
}

// CRA NOTE: REACT_APP_* variables are read at build time only.
// After changing env values, you must rebuild the frontend bundle.
const BUGSNAG_API_KEY = process.env.REACT_APP_BUGSNAG_API_KEY || '';

// Determine release stage from environment
// Priority: REACT_APP_ENVIRONMENT > NODE_ENV > default to 'development'
// Supports: development, staging, testing, production
// Note: REACT_APP_ENVIRONMENT is used for React apps (must be prefixed with REACT_APP_)
const getReleaseStage = (): string => {
  // Check for explicit REACT_APP_ENVIRONMENT variable first (React-specific)
  // Then check NODE_ENV (standard Node.js variable)
  // Fallback to development
  const env = process.env.REACT_APP_ENVIRONMENT 
    || process.env.NODE_ENV 
    || 'development';
  
  const normalizedEnv = env.toLowerCase().trim();
  
  // Map common variations to valid environment names
  const envMap: Record<string, string> = {
    'dev': 'development',
    'prod': 'production',
    'test': 'testing',
    'stage': 'staging',
  };
  
  // Check if it's a mapped variation
  const mappedEnv = envMap[normalizedEnv];
  if (mappedEnv) {
    return mappedEnv;
  }
  
  // Validate and normalize environment names
  const validEnvironments = ['development', 'staging', 'testing', 'production'];
  if (validEnvironments.includes(normalizedEnv)) {
    return normalizedEnv;
  }
  
  // If environment doesn't match valid values, default to development
  console.warn(`[Bugsnag] Invalid environment "${env}", defaulting to "development"`);
  return 'development';
};

// Initialize Bugsnag
const releaseStage = getReleaseStage();
const enabledReleaseStages = ['development', 'staging', 'production', 'testing']; // Report errors in all environments including testing

// Prefer human-readable version for Bugsnag grouping, keep git in metadata.
const appVersion = process.env.REACT_APP_VERSION || process.env.REACT_APP_GITHUB_COMMIT || 'unknown';

// Git info detection for frontend (must be set via environment variables at build time)
// Note: Frontend runs in browser, so we can't use Node.js modules like child_process
// Git info must be set via REACT_APP_GIT_BRANCH and REACT_APP_GIT_SHA environment variables
const getGitInfo = () => {
  // Get from environment variables (set at build/start time)
  const gitBranch = process.env.REACT_APP_GITHUB_BRANCH 
    || process.env.REACT_APP_GIT_BRANCH 
    || process.env.REACT_APP_VCS_BRANCH 
    || 'unknown';
  const gitSha = process.env.REACT_APP_GITHUB_COMMIT 
    || process.env.REACT_APP_GIT_SHA 
    || process.env.REACT_APP_VCS_COMMIT 
    || 'unknown';
  
  const result = {
    branch: gitBranch,
    sha: gitSha
  };
  
  // Log final values for debugging
  console.log('[Bugsnag] Git info:', {
    branch: result.branch,
    sha: result.sha !== 'unknown' ? result.sha.substring(0, 7) + '...' : 'unknown',
    source: process.env.REACT_APP_GIT_BRANCH || process.env.REACT_APP_GIT_SHA 
      ? 'environment variables' 
      : 'not set (use REACT_APP_GIT_BRANCH and REACT_APP_GIT_SHA)'
  });
  
  return result;
};

const { branch: gitBranch, sha: gitSha } = getGitInfo();

const getCurrentRoute = () => {
  if (typeof window === 'undefined') return 'unknown';
  return `${window.location.pathname}${window.location.search}`;
};

const getPreviousRoute = () => {
  if (typeof window === 'undefined') return null;
  return window.__LAST_ROUTE__ ?? null;
};

const getFeatureContext = () => {
  if (typeof window === 'undefined') return [];
  const flags = window.__ENABLED_FEATURES__;
  if (!flags) return [];
  if (Array.isArray(flags)) return flags;
  return Object.entries(flags)
    .filter(([, enabled]) => !!enabled)
    .map(([key]) => key);
};

const getUserContext = () => {
  if (typeof window === 'undefined') return null;
  return window.__CURRENT_USER__ ?? null;
};

const isNetworkNoise = (errorClass: string, message: string) => {
  const noisePatterns = ['Failed to fetch', 'Network request failed', 'AbortError'];
  return (
    errorClass === 'AbortError' ||
    noisePatterns.some((pattern) => message.includes(pattern)) ||
    (typeof navigator !== 'undefined' && navigator.onLine === false)
  );
};

const isValidationMessage = (message: string) => message.toLowerCase().includes('validation');

// Avoid initializing Bugsnag without a valid API key.
let bugsnagClient: any;
if (BUGSNAG_API_KEY) {
  bugsnagClient = Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage: releaseStage,
    enabledReleaseStages: enabledReleaseStages,
    appVersion,
    collectUserIp: false, // Privacy: don't collect IP addresses
    onError: (event) => {
      const primaryError = event.errors[0];
      const errorMessage = primaryError?.errorMessage || '';
      const errorClass = primaryError?.errorClass || '';

      // Log all errors for debugging (especially test errors)
      if (errorMessage.includes('[BugsnagTest]')) {
        console.log('[Bugsnag] onError callback - TEST ERROR DETECTED:', {
          errorMessage,
          errorClass,
          unhandled: event.unhandled,
          initialSeverity: event.severity,
        });
      }

    event.addMetadata('navigation', {
      route: getCurrentRoute(),
      previousRoute: getPreviousRoute(),
      features: getFeatureContext(),
    });

    const userContext = getUserContext();
    if (userContext) {
      const userId = userContext.id ? userContext.id.toString() : undefined;
      event.setUser(userId, userContext.email, userContext.name);
      event.addMetadata('userContext', {
        role: userContext.role,
        tenant: userContext.tenant,
        state: userContext.state,
      });
    }

    if (isNetworkNoise(errorClass, errorMessage)) {
      if (errorMessage.includes('[BugsnagTest]')) {
        console.log('[Bugsnag] TEST ERROR would be filtered as network noise - NOT FILTERING');
        // Don't filter test errors even if they look like network noise
      } else {
        event.addMetadata('noise', { reason: 'Network noise filtered' });
        return false;
      }
    }

    if (!event.unhandled) {
      event.severity = isValidationMessage(errorMessage) ? 'warning' : 'info';
    } else {
      event.severity = 'error';
    }

    event.addMetadata('classification', {
      handled: !event.unhandled,
      severity: event.severity,
    });

    if (!event.context) {
      event.context = getCurrentRoute();
    }

    // Log final state for test errors
    if (errorMessage.includes('[BugsnagTest]')) {
      console.log('[Bugsnag] onError callback - TEST ERROR FINAL STATE:', {
        errorMessage,
        finalSeverity: event.severity,
        unhandled: event.unhandled,
        willBeSent: true,
      });
    }

      return true; // Return true to send the error, false to ignore it
    },
    onSession: (session) => {
      console.log('[Bugsnag] Session started:', session.id);
    },
  });
} else {
  console.warn('[Bugsnag] WARNING: REACT_APP_BUGSNAG_API_KEY not set. Bugsnag is disabled.');
  bugsnagClient = {
    notify: () => undefined,
    getPlugin: () => undefined,
    setUser: () => undefined,
    addMetadata: () => undefined,
    leaveBreadcrumb: () => undefined,
    startSession: () => undefined,
  };
}

// Log Bugsnag initialization status
console.log('[Bugsnag] Initialized with API key:', BUGSNAG_API_KEY ? `${BUGSNAG_API_KEY.substring(0, 8)}...` : 'NOT SET');
console.log('[Bugsnag] Release stage:', releaseStage);
console.log('[Bugsnag] Enabled release stages:', enabledReleaseStages);
console.log('[Bugsnag] Release stage is enabled:', enabledReleaseStages.includes(releaseStage));

// Verify release stage is enabled (sessions are automatically tracked if release stage matches)
if (!enabledReleaseStages.includes(releaseStage)) {
  console.warn(`[Bugsnag] WARNING: Release stage "${releaseStage}" is not in enabledReleaseStages. Sessions and errors may not be sent.`);
  console.warn(`[Bugsnag] Current release stage: "${releaseStage}"`);
  console.warn(`[Bugsnag] Enabled stages: ${enabledReleaseStages.join(', ')}`);
}

// Export the Bugsnag client
export default bugsnagClient;

